import {
    AttributeDefinitionType,
    AttributesData,
    JSONAnnotationDataType,
    NotificationPropsType,
    TaskAttribute,
    TaskMode,
    UpdatedAttributesData,
} from 'Types/Tasks.types'
import { AttributeContext } from '../AttributeManager'
import {
    AttributeGroupConfigExtended,
    AttributeGroupEvent,
    BaseAttributeGroup,
} from './BaseAttributeGroup'
import { AttributeValueType } from 'Types/Attribute.types'
import { FormInstance } from 'antd/lib'
import { createReactElement } from 'shared/utils/react.utils'
import { AttributesFormWrapper } from 'Components/tasks/Attributes/AttributesFormWrapper'
import { convertFlatToWrappedAttributes, showAttribute } from 'Utils/attributes.utils'

export class TargetedAttributeGroup extends BaseAttributeGroup {
    attributeDefinitionType = AttributeDefinitionType.TARGETED_ATTRIBUTES as const
    constructor(attributeContext: AttributeContext, config: AttributeGroupConfigExtended) {
        super(attributeContext, config)
        this.build()
        const draftOutputContainer = this.attributeContext.taskState?.getDraftOutputContainer()
        if (!draftOutputContainer) {
            return
        }
        const data = draftOutputContainer.data.data as JSONAnnotationDataType
        const attributesData = data.response.json_annotations?.[this.groupKey]?.attributes ?? {}
        this.updateAreMandatoryAttributesFilled(attributesData)
    }

    public build(): void {
        const draftOutputContainer = this.attributeContext.taskState?.getDraftOutputContainer()
        if (!draftOutputContainer) {
            return
        }
        const data = draftOutputContainer.data.data as JSONAnnotationDataType

        const config = this.attributeContext.taskConfig

        const props = {
            groupKey: this.groupKey,
            attributes: this.attributes,
            onUpdateAttribute: (attrName: string, val: AttributeValueType) => {
                if (val === undefined) {
                    if (data.response.json_annotations?.[this.groupKey]?.attributes[attrName]) {
                        delete data.response.json_annotations[this.groupKey]?.attributes[attrName]
                        if (
                            Object.keys(data.response.json_annotations?.[this.groupKey]?.attributes)
                                .length === 0
                        ) {
                            delete data.response.json_annotations[this.groupKey]
                        }
                    }
                } else {
                    if (data.response.json_annotations?.[this.groupKey]?.attributes[attrName]) {
                        data.response.json_annotations[this.groupKey].attributes[attrName].value =
                            val
                    } else {
                        this.initJSONPathAttribute(attrName, val, this.attributes)
                    }
                }

                this.attributeContext.saveTask({
                    onComplete: (notifications?: NotificationPropsType) =>
                        this.attributeContext.studio.exitTask(notifications),
                })
                const attributesData =
                    data.response.json_annotations?.[this.groupKey]?.attributes ?? {}
                this.updateAreMandatoryAttributesFilled(attributesData)
                this.emit(AttributeGroupEvent.Change)
                this.render()
            },
            getValues: () =>
                convertFlatToWrappedAttributes(
                    data.response.json_annotations?.[this.groupKey]?.attributes ?? {}
                ),
            attributesOnUI: this.attributeContext.attributesOnUI,
            setForm: (form: FormInstance) => {
                this.form = form
            },
        }

        this.element = createReactElement(AttributesFormWrapper, {
            ...props,
            isReadOnly: () =>
                config.mode !== TaskMode.Triager && this.attributeContext.task.isReadOnly,
            headerComponent:
                this.props?.header ?? this.attributeContext.task.createAttributeHeader(),
            getFeedback: this.attributeContext.task.contextHandler.getFeedback,
            mode: config.mode,
            isTrainingMode: this.attributeContext.task.contextHandler.isTrainingMode,
        })
    }

    getVisibleMandatoryAttributes = (attributesData: AttributesData) => {
        const attributesOnUI: { [key: string]: 1 } = {}
        const toUpdateRef = { current: {} }

        return this.mandatoryAttributes.filter(attr => {
            const isVisible = showAttribute(
                this.attributes,
                attr,
                attributesOnUI,
                attributesData,
                toUpdateRef
            )
            attributesOnUI[attr.name] = 1
            return isVisible
        })
    }

    updateAreMandatoryAttributesFilled(attributesData?: UpdatedAttributesData) {
        if (!this.mandatoryAttributes.length) {
            this.areMandatoryAttributesFilled = true
            return
        }
        if (!attributesData) {
            this.areMandatoryAttributesFilled = false
            return
        }
        const wrappedAttributesData = convertFlatToWrappedAttributes(attributesData)
        const visibleMandatoryAttributes = this.getVisibleMandatoryAttributes(wrappedAttributesData)
        this.areMandatoryAttributesFilled = !visibleMandatoryAttributes.some(attr => {
            return attributesData[attr.name] === undefined
        })
    }

    private initJSONPathAttribute(
        attrName: string,
        val: NonNullable<AttributeValueType>,
        attributes: TaskAttribute[]
    ) {
        const draftOutputContainer = this.attributeContext.taskState?.getDraftOutputContainer()
        if (!draftOutputContainer) {
            return
        }
        const data = draftOutputContainer.data.data as JSONAnnotationDataType

        if (!data.response.json_annotations) {
            data.response.json_annotations = {}
        }
        if (!data.response.json_annotations[this.groupKey]) {
            data.response.json_annotations[this.groupKey] = { attributes: {} }
        }

        data.response.json_annotations[this.groupKey]['attributes'][attrName] = {
            value_id: crypto.randomUUID(),
            value: val,
            value_type: attributes.find(attr => attr.name === attrName)!.value_definition
                .value_type,
        }
    }

    validate = async () => {
        if (!this.attributes.length) {
            return true
        }
        const groupValues = this.getElement()?.element?.props.props.getValues() ?? {}

        const mandatoryVisibleAttributes = this.getVisibleMandatoryAttributes(groupValues)

        const isInvalid = mandatoryVisibleAttributes.some(attr => {
            const value = groupValues[attr.name]
            if (value === undefined) {
                return true
            }
            return false
        })

        // if form is active, we validate forms and show errors
        if (this.form) {
            await this.form.validateFields().catch(() => false)
        }

        return !isInvalid
    }

    removeAllAttributes() {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as JSONAnnotationDataType
        if (!data) {
            return
        }
        const groupKey = this.groupKey
        if (data.response.json_annotations?.[groupKey]) {
            delete data.response.json_annotations[groupKey]
        }
    }
}
